import React, { useState } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Stack,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { FormControlLabel, Switch, Tooltip } from '@mui/material';
import DeleteWithCountdownButton from './DeleteWithCountdownButton';
import UpgradeContentHint from './UpgradeContentHint';
import { currencyFormatter } from '../../../../util/formatHelper';

// Shown wherever the count pencil is limited to increases because the backend gates the plain
// refunding reduction (free-reduction window, no ticket insurance, no override right).
const REDUCTION_BLOCKED_HINT =
  'Weniger Teilnehmer gehen innerhalb von 7 Tagen ohne Ticketversicherung nur über "Spontan weniger Personen" (MOP-Show-Gutschein).';

function BookingLineItemsTable({
  booking,
  editingLineItem,
  lineItemCount,
  oldLineItemCount,
  availableCapacity,
  minParticipants,
  maxParticipants,
  canOverbook = false,
  overbookEnabled = false,
  onOverbookToggle,
  editingUpsell,
  upsellCount,
  upsellType,
  editingSpecialLineItem,
  specialLineItemCount,
  isTodayorFutue,
  hasUpdateRights,
  isEditable,
  hasUnlimitedEditRights,
  hasPastCancelRights,
  canReduceParticipants = true,
  onStartEditingLineItem,
  onStopEditingLineItem,
  // True while a participant change is being saved. Confirming twice would add or cancel
  // the participants a second time, so the stepper is locked for the duration.
  participantSubmitting = false,
  onSetLineItemCount,
  onStartEditingUpsell,
  onStopEditingUpsell,
  onSetUpsellCount,
  onStartEditingSpecialLineItem,
  onStopEditingSpecialLineItem,
  onSetSpecialLineItemCount,
  onRemovePackage,
  onRemoveUpsell,
  onRemoveSpecialLineItem,
  onSetSelectedLineItem,
  onSetLineItemPaymentModalOpen,
  onRemoveVoucher,
  hasOverridePricesRight,
  onOverrideLineItemPrice,
  addSpecialItemOpen,
  onAddSpecialItem,
  onCancelAddSpecialItem,
}) {
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceInputValue, setPriceInputValue] = useState('');
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemCount, setNewItemCount] = useState('1');
  const [newItemPrice, setNewItemPrice] = useState('0');

  const overridableTypes = ['booking', 'package', 'upsell', 'special'];
  // Allow price edits when no real payment is linked (relatedPaymentId).
  // Items auto-marked as paid because price=0 (no actual payment) can still be overridden.
  const isFullyUnpaid = item =>
    (item.paymentReferences || []).every(ref => !ref.relatedPaymentId);

  const canEditPrice = item =>
    hasOverridePricesRight &&
    overridableTypes.includes(item.type) &&
    isFullyUnpaid(item);

  const handleStartEditPrice = item => {
    setEditingPriceId(item._id);
    setPriceInputValue(String(item.price).replace('.', ','));
  };

  const handleConfirmPriceEdit = async item => {
    const newPrice = parseFloat(priceInputValue.replace(',', '.'));
    if (Number.isFinite(newPrice) && newPrice >= 0) {
      await onOverrideLineItemPrice(item._id, newPrice);
    }
    setEditingPriceId(null);
    setPriceInputValue('');
  };

  const handleCancelPriceEdit = () => {
    setEditingPriceId(null);
    setPriceInputValue('');
  };

  const hasPayableAmount = parseFloat(booking?.totalAmount || 0) > 0;
  const isEffectivelyPaid = booking.fullyPaid || !hasPayableAmount;
  const getResourceVoucherCode = item => {
    if (!item) return '';
    const directCode = String(item.resourceVoucherCode || '').trim();
    if (directCode) return directCode.toUpperCase();

    const description = String(item.description || '');
    const match = description.match(/RV-([A-Z0-9]+)/i);
    return match?.[1] ? match[1].toUpperCase() : '';
  };
  const isSingleResourceVoucherRedemption = item => {
    if (!item) return false;
    if (!Array.isArray(booking?.resourceVoucherIds) || booking.resourceVoucherIds.length === 0) {
      return false;
    }

    if (item.type === 'resourceVoucher') return true;
    if (item.type !== 'booking') return false;

    return Boolean(getResourceVoucherCode(item));
  };
  const normalizeNumber = value => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return null;
      const n = Number(raw.replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  const formatPrice = value => {
    const n = normalizeNumber(value);
    return typeof n === 'number' ? n.toFixed(2).replace('.', ',') : '0,00';
  };
  const formatCurrency = value => currencyFormatter.format(normalizeNumber(value) ?? 0);

  // Calculate total of non-voucher line items for correct voucher display
  const activeLineItems = (booking.lineItems || []).filter(item => item.status === 'active');
  const nonVoucherTotal = activeLineItems
    .filter(item => item.type !== 'voucherRedemption')
    .reduce((sum, item) => sum + (item.price * item.count), 0);

  // Calculate effective voucher amounts (capped to available booking total)
  const getEffectiveVoucherPrice = item => {
    if (item.type !== 'voucherRedemption') return item.price;
    
    // Get all voucher line items before this one
    const voucherItems = activeLineItems.filter(i => i.type === 'voucherRedemption');
    const thisVoucherIndex = voucherItems.findIndex(v => v._id === item._id);
    
    // Calculate how much has been used by previous vouchers
    let usedByPreviousVouchers = 0;
    for (let i = 0; i < thisVoucherIndex; i++) {
      const prev = voucherItems[i];
      const prevAbsPrice = Math.abs(prev.price * prev.count);
      const prevAvailable = Math.max(0, nonVoucherTotal - usedByPreviousVouchers);
      usedByPreviousVouchers += Math.min(prevAbsPrice, prevAvailable);
    }
    
    // Calculate available amount for this voucher
    const availableForThisVoucher = Math.max(0, nonVoucherTotal - usedByPreviousVouchers);
    const voucherAbsPrice = Math.abs(item.price * item.count);
    const effectiveAmount = Math.min(voucherAbsPrice, availableForThisVoucher);
    
    // Return negative price per unit
    return item.count > 0 ? -(effectiveAmount / item.count) : 0;
  };
  const activeLineItemsTotal = activeLineItems.reduce((sum, item) => {
    const unitPrice =
      item.type === 'voucherRedemption'
        ? getEffectiveVoucherPrice(item)
        : (normalizeNumber(item.price) ?? 0);
    const count = normalizeNumber(item.count) ?? 0;
    return sum + unitPrice * count;
  }, 0);
  const renderLineItemActions = item => {
    if (editingLineItem === item._id) {
      const addedParticipants = Math.max(0, (lineItemCount || 0) - (oldLineItemCount || 0));
      const remainingFree = Math.max(0, (availableCapacity || 0) - addedParticipants);
      const overbookActive = overbookEnabled && canOverbook;
      const overbookedBy = overbookActive
        ? Math.max(0, addedParticipants - (availableCapacity || 0))
        : 0;
      // Gated reduction: the pencil still adds participants, but the booked count is the floor.
      // Unlike the minParticipants limit below, no staff right lifts it.
      const hardMin = canReduceParticipants ? null : (oldLineItemCount ?? item.count ?? 0);
      const atHardMin = hardMin !== null && lineItemCount <= hardMin;
      const decrementButton = (
        <IconButton
          size='small'
          onClick={() => {
            const newCount = lineItemCount - 1;
            const othersCount = booking.participantCount - oldLineItemCount;
            const minLimit = minParticipants || 1;
            // Ensure we don't go below 0, and don't drop total below minLimit
            const minAllowedForThisItem = Math.max(0, minLimit - othersCount);
            onSetLineItemCount(
              Math.max(hardMin ?? -Infinity, minAllowedForThisItem, newCount)
            );
          }}
          disabled={
            booking.status === 'noShow' ||
            atHardMin ||
            (!hasUnlimitedEditRights &&
            (lineItemCount <= 0 ||
              (minParticipants &&
                booking.participantCount - oldLineItemCount + (lineItemCount - 1) <
                  minParticipants)))
          }
          sx={{
            '&.Mui-disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          <RemoveIcon />
        </IconButton>
      );
      return (
        <Stack direction='row' spacing={1} alignItems='center'>
          {atHardMin ? (
            // A disabled IconButton fires no pointer events, so the tooltip needs its own wrapper.
            <Tooltip title={REDUCTION_BLOCKED_HINT}>
              <span style={{ display: 'inline-flex' }}>{decrementButton}</span>
            </Tooltip>
          ) : (
            decrementButton
          )}

          <Stack spacing={0} alignItems='center' sx={{ minWidth: 34 }}>
            <Typography>{lineItemCount}</Typography>
            {overbookedBy > 0 ? (
              <Stack direction='row' alignItems='center' spacing={0.3}>
                <GroupAddIcon sx={{ fontSize: '0.8rem', color: 'error.main' }} />
                <Typography
                  variant='caption'
                  sx={{ lineHeight: 1.1, color: 'error.main', fontWeight: 'bold' }}
                >
                  +{overbookedBy}
                </Typography>
              </Stack>
            ) : (
              <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.1 }}>
                {remainingFree} frei
              </Typography>
            )}
          </Stack>

          <IconButton
            size='small'
            onClick={() => {
              const newCount = lineItemCount + 1;
              const othersCount = booking.participantCount - oldLineItemCount;
              const maxTotal = maxParticipants || Infinity;
              const maxAllowedForThisItemByTotal = overbookActive
                ? Infinity
                : maxTotal - othersCount;
              const limitByCapacity = overbookActive
                ? Infinity
                : oldLineItemCount + availableCapacity;

              const finalMaxLimit = Math.min(limitByCapacity, maxAllowedForThisItemByTotal);
              onSetLineItemCount(Math.min(finalMaxLimit, newCount));
            }}
            disabled={
              booking.status === 'noShow' ||
              (!isEditable && !hasUnlimitedEditRights) ||
              (!hasUnlimitedEditRights && !overbookActive &&
                (lineItemCount >= oldLineItemCount + availableCapacity ||
                  (maxParticipants &&
                    booking.participantCount - oldLineItemCount + (lineItemCount + 1) >
                      maxParticipants)))
            }
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            <AddIcon />
          </IconButton>
          {canOverbook && (
            <Tooltip title='Überbuchen erlauben – Kapazitätsgrenzen werden ignoriert.'>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size='small'
                    checked={!!overbookEnabled}
                    onChange={e => onOverbookToggle?.(e.target.checked)}
                    color='warning'
                  />
                }
                label={
                  <Typography variant='caption' sx={{ color: 'warning.main' }}>
                    Überbuchen
                  </Typography>
                }
              />
            </Tooltip>
          )}
          <IconButton
            size='small'
            color='success'
            disabled={participantSubmitting}
            onClick={onStopEditingLineItem}
          >
            {participantSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          </IconButton>
        </Stack>
      );
    }

    if (editingUpsell === item._id) {
      const normalizeId = value => (typeof value === 'string' ? value : value?.toString?.() || '');
      const bookingUpsell = booking.upsells.find(
        upsell => normalizeId(upsell.upgradeId) === normalizeId(item.referenceId)
      );
      const maxFromUpsell =
        typeof bookingUpsell?.maximumCount === 'number' &&
        Number.isFinite(bookingUpsell.maximumCount)
          ? Math.floor(bookingUpsell.maximumCount)
          : null;
      const participantLimit =
        typeof booking.participantCount === 'number' && Number.isFinite(booking.participantCount)
          ? booking.participantCount
          : 0;
      const baseMax = upsellType === 'freeSelectMaxPersons' ? participantLimit : maxFromUpsell;
      const resolvedMaxCount =
        typeof baseMax === 'number' && baseMax >= 0 && Number.isFinite(baseMax)
          ? baseMax
          : upsellType === 'freeSelectMaxPersons'
            ? participantLimit
            : 99;
      return (
        <Stack direction='row' spacing={1} alignItems='center'>
          <IconButton
            size='small'
            onClick={() => onSetUpsellCount(Math.max(0, upsellCount - 1))}
            disabled={booking.status === 'noShow' || (!hasUnlimitedEditRights && upsellCount <= 0)}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            <RemoveIcon />
          </IconButton>
          <Typography>{upsellCount}</Typography>
          <IconButton
            size='small'
            onClick={() => onSetUpsellCount(Math.min(resolvedMaxCount, upsellCount + 1))}
            disabled={booking.status === 'noShow' || (!hasUnlimitedEditRights && upsellCount >= resolvedMaxCount)}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            <AddIcon />
          </IconButton>
          <IconButton size='small' color='success' onClick={onStopEditingUpsell}>
            <CheckCircleIcon />
          </IconButton>
        </Stack>
      );
    }

    if (editingSpecialLineItem === item._id) {
      return (
        <Stack direction='row' spacing={1} alignItems='center'>
          <IconButton
            size='small'
            onClick={() => onSetSpecialLineItemCount(Math.max(0, specialLineItemCount - 1))}
            disabled={booking.status === 'noShow' || (!hasUnlimitedEditRights && specialLineItemCount <= 0)}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            <RemoveIcon />
          </IconButton>
          <Typography>{specialLineItemCount}</Typography>
          <IconButton
            size='small'
            onClick={() => onSetSpecialLineItemCount(specialLineItemCount + 1)}
            disabled={booking.status === 'noShow'}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}
          >
            <AddIcon />
          </IconButton>
          <IconButton size='small' color='success' onClick={onStopEditingSpecialLineItem}>
            <CheckCircleIcon />
          </IconButton>
        </Stack>
      );
    }

    // Default display with action buttons
    return (
      <>
        {item.count}{' '}
        {item.type === 'booking' &&
          (isTodayorFutue || hasUnlimitedEditRights || hasPastCancelRights) &&
          hasUpdateRights &&
          !(Array.isArray(booking.resourceVoucherIds) && booking.resourceVoucherIds.length > 0) &&
          (() => {
            const disabled =
              booking.status === 'noShow' ||
              (!isEditable && !hasUnlimitedEditRights && !hasPastCancelRights);
            const editButton = (
              <IconButton
                size='small'
                color='primary'
                onClick={() => onStartEditingLineItem(item)}
                disabled={disabled}
              >
                <EditIcon />
              </IconButton>
            );
            // Explain up front that the pencil can only add participants here.
            if (disabled || canReduceParticipants) return editButton;
            return (
              <Tooltip title={`Teilnehmerzahl erhöhen. ${REDUCTION_BLOCKED_HINT}`}>
                <span style={{ display: 'inline-flex' }}>{editButton}</span>
              </Tooltip>
            );
          })()}
        {isSingleResourceVoucherRedemption(item) &&
          (isTodayorFutue || hasUnlimitedEditRights) &&
          hasUpdateRights && (
            <DeleteWithCountdownButton
              onConfirm={() => onRemoveVoucher(item)}
              disabled={!isEditable}
            />
          )}
        {item.type === 'package' &&
          (isTodayorFutue || hasUnlimitedEditRights) &&
          hasUpdateRights && (
            <DeleteWithCountdownButton
              onConfirm={() => onRemovePackage(item)}
              disabled={!isEditable}
            />
          )}
        {item.type === 'upsell' &&
          (isTodayorFutue || hasUnlimitedEditRights) &&
          hasUpdateRights &&
          (() => {
            const upsell = booking.upsells.find(u => u.upgradeId === item.referenceId);
            const upsellType = upsell?.type || null;
            const isNonCancelableUpsell =
              upsellType === 'matchPersonsNoCancel' ||
              upsellType === 'matchLanesNoCancel' ||
              upsellType === 'freeSelectNoCancel';
            if (isNonCancelableUpsell && !hasUnlimitedEditRights) return null;
            return (
              <>
                {upsellType !== 'matchPersons' && (
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={() => onStartEditingUpsell(item, upsellType)}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <DeleteWithCountdownButton
                  onConfirm={() => onRemoveUpsell(item)}
                  disabled={!isEditable}
                />
              </>
            );
          })()}
        {item.type === 'special' &&
          (isTodayorFutue || hasUnlimitedEditRights) &&
          hasUpdateRights && (
            <>
              <IconButton
                size='small'
                color='primary'
                onClick={() => onStartEditingSpecialLineItem(item)}
              >
                <EditIcon />
              </IconButton>
              <DeleteWithCountdownButton
                onConfirm={() => onRemoveSpecialLineItem(item)}
                disabled={!isEditable}
              />
            </>
          )}
      </>
    );
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Beschreibung</TableCell>
            <TableCell>Anzahl</TableCell>
            <TableCell>Preis</TableCell>
            {!isEffectivelyPaid && <TableCell>Unbezahlt</TableCell>}
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {booking.lineItems
            .filter(item => item.status === 'active')
            .map(item => (
              <TableRow key={item._id}>
                <TableCell>
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <span>{item.description}</span>
                    <UpgradeContentHint item={item} />
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderLineItemActions(item)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {editingPriceId === item._id ? (
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <TextField
                        size='small'
                        value={priceInputValue}
                        onChange={e => setPriceInputValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleConfirmPriceEdit(item);
                          if (e.key === 'Escape') handleCancelPriceEdit();
                        }}
                        inputProps={{ style: { width: 80, textAlign: 'right' } }}
                        autoFocus
                      />
                      <Typography variant='body2'>€</Typography>
                      <IconButton size='small' onClick={() => handleConfirmPriceEdit(item)}>
                        <CheckCircleIcon fontSize='small' color='success' />
                      </IconButton>
                    </Stack>
                  ) : (
                    <>
                      {/* show original per-unit price struck through when price differs */}
                      <Stack direction='row' spacing={0.5} alignItems='center'>
                        <Typography>
                          <b>{formatPrice(item.type === 'voucherRedemption' ? getEffectiveVoucherPrice(item) : item.price)} €</b>
                          {(() => {
                            if (item.type === 'voucherRedemption') return null;
                            const original = normalizeNumber(item.originalPrice);
                            const current = normalizeNumber(item.price);
                            if (original == null || current == null) return null;
                            if (original === current) return null;
                            // Derive the discount as both an absolute amount and a percentage
                            // so cashiers no longer have to guess the percentage at the register.
                            const discountAmount = original - current;
                            const discountPercent = original !== 0 ? (discountAmount / original) * 100 : null;
                            const formatPercent = value => {
                              const rounded = Math.round(value * 10) / 10;
                              return (Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1).replace('.', ',')) + ' %';
                            };
                            const tooltipText =
                              discountPercent != null
                                ? `Rabatt: ${formatPercent(discountPercent)} (${formatPrice(discountAmount)} €)`
                                : `Rabatt: ${formatPrice(discountAmount)} €`;
                            return (
                            <Tooltip title={tooltipText} arrow>
                            <span
                              style={{
                                textDecoration: 'line-through',
                                color: 'lightgrey',
                                fontSize: '0.8rem',
                                paddingLeft: '10px',
                                cursor: 'help',
                              }}
                            >
                              {formatPrice(item.originalPrice)} €
                            </span>
                            </Tooltip>
                            );
                          })()}
                        </Typography>
                        {canEditPrice(item) && (
                          <IconButton size='small' onClick={() => handleStartEditPrice(item)} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        )}
                      </Stack>
                      {/* show discount info and code */}
                      {(item.appliedDiscountCode || item.discountAmount) && (
                        <Typography
                          variant='caption'
                          sx={{ display: 'block', color: 'text.secondary' }}
                        >
                          {item.appliedDiscountCode ? `Rabattcode: ${item.appliedDiscountCode}` : ''}
                          {item.appliedDiscountCode && item.discountAmount ? ' · ' : ''}
                          {item.discountAmount
                            ? `Ersparnis: ${formatPrice(item.discountAmount)} €`
                            : ''}
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
                {!isEffectivelyPaid && (
                  <TableCell>
                    <Typography
                      color={
                        (Array.isArray(item?.paymentReferences) ? item.paymentReferences : [])
                          .filter(ref => !ref.isPaid)
                          .reduce((sum, ref) => sum + ref.count, 0) > 0
                          ? 'error'
                          : 'inherit'
                      }
                      fontWeight={
                        (Array.isArray(item?.paymentReferences) ? item.paymentReferences : [])
                          .filter(ref => !ref.isPaid)
                          .reduce((sum, ref) => sum + ref.count, 0) > 0
                          ? 'bold'
                          : 'normal'
                      }
                    >
                      {(Array.isArray(item?.paymentReferences) ? item.paymentReferences : [])
                        .filter(ref => !ref.isPaid)
                        .reduce((sum, ref) => sum + ref.count, 0)
                        .toFixed(0)}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <IconButton
                    size='small'
                    onClick={() => {
                      onSetSelectedLineItem(item);
                      onSetLineItemPaymentModalOpen(true);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <InfoIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          {addSpecialItemOpen && (
            <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
              <TableCell sx={{ py: 1 }}>
                <TextField
                  placeholder='Beschreibung'
                  size='small'
                  fullWidth
                  value={newItemDesc}
                  onChange={e => setNewItemDesc(e.target.value)}
                  variant='standard'
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setNewItemDesc('');
                      setNewItemCount('1');
                      setNewItemPrice('0');
                      onCancelAddSpecialItem();
                    }
                  }}
                />
              </TableCell>
              <TableCell sx={{ py: 1 }}>
                <TextField
                  size='small'
                  type='number'
                  value={newItemCount}
                  onChange={e => setNewItemCount(e.target.value)}
                  variant='standard'
                  inputProps={{ min: 1, step: 1, style: { width: 40, textAlign: 'center' } }}
                />
              </TableCell>
              <TableCell sx={{ py: 1 }}>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <TextField
                    size='small'
                    value={newItemPrice}
                    onChange={e => setNewItemPrice(e.target.value)}
                    variant='standard'
                    inputProps={{ style: { width: 60, textAlign: 'right' } }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const count = parseInt(newItemCount, 10);
                        const price = parseFloat(newItemPrice.replace(',', '.'));
                        if (newItemDesc.trim() && Number.isFinite(count) && count > 0 && Number.isFinite(price) && price >= 0) {
                          onAddSpecialItem(newItemDesc.trim(), count, price);
                          setNewItemDesc('');
                          setNewItemCount('1');
                          setNewItemPrice('0');
                        }
                      }
                    }}
                  />
                  <Typography variant='body2'>€</Typography>
                </Stack>
              </TableCell>
              {!isEffectivelyPaid && <TableCell sx={{ py: 1 }} />}
              <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                <IconButton
                  size='small'
                  color='success'
                  disabled={!newItemDesc.trim()}
                  onClick={() => {
                    const count = parseInt(newItemCount, 10);
                    const price = parseFloat(newItemPrice.replace(',', '.'));
                    if (!newItemDesc.trim() || !Number.isFinite(count) || count <= 0 || !Number.isFinite(price) || price < 0) return;
                    onAddSpecialItem(newItemDesc.trim(), count, price);
                    setNewItemDesc('');
                    setNewItemCount('1');
                    setNewItemPrice('0');
                  }}
                >
                  <CheckCircleIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => {
                    setNewItemDesc('');
                    setNewItemCount('1');
                    setNewItemPrice('0');
                    onCancelAddSpecialItem();
                  }}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell sx={{ pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant='body2' color='text.secondary'>
                Gesamtsumme
              </Typography>
            </TableCell>
            <TableCell sx={{ pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }} />
            <TableCell
              sx={{
                pt: 2.5,
                whiteSpace: 'nowrap',
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant='body1' fontWeight={700}>
                {formatCurrency(activeLineItemsTotal)}
              </Typography>
            </TableCell>
            {!isEffectivelyPaid && (
              <TableCell sx={{ pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }} />
            )}
            <TableCell sx={{ pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }} />
          </TableRow>
        </TableBody>
      </Table>
      {booking.lineItems.some(item => {
        const refs = Array.isArray(item?.paymentReferences) ? item.paymentReferences : [];
        return refs.some(ref => (ref?.countRefunded || 0) > 0);
      }) && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
              },
              flexDirection: 'row-reverse',
              '& .MuiAccordionSummary-expandIconWrapper': {
                marginRight: 1,
                marginLeft: 0,
              },
            }}
          >
            <Typography>Stornierte Positionen</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer width='100%'>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell>Anzahl</TableCell>
                    <TableCell>Preis</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {booking.lineItems
                    .filter(item => {
                      const refs = Array.isArray(item?.paymentReferences) ? item.paymentReferences : [];
                      return refs.some(ref => (ref?.countRefunded || 0) > 0);
                    })
                    .map(item => (
                      <TableRow key={item._id}>
                        <TableCell
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.secondary',
                          }}
                        >
                          {item.description}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {(Array.isArray(item?.paymentReferences) ? item.paymentReferences : [])
                            .filter(ref => ref.countRefunded > 0)
                            .reduce((sum, ref) => sum + ref.countRefunded, 0)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {item.price.toFixed(2).replace('.', ',')} €
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}
    </TableContainer>
  );
}

export default BookingLineItemsTable;
